import { createFileRoute, useLocation } from "@tanstack/react-router";
import { useState, useEffect, useMemo, useCallback } from "react";
import { z } from "zod";
import { toast } from "sonner";
import type { UseFormSetError } from "react-hook-form";
import dayjs from "dayjs";
import type { DateRange } from "react-day-picker";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import {
  SidebarNav,
  type SidebarNavEntry,
} from "@/components/profile-management/sidebar-nav";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { DateSelector } from "@/components/crud/filters/DateSelector";

import { useDebounce } from "@/hooks/use-debounce";
import { applyServerErrors } from "@/lib/apply-server-errors";
import { formatError } from "@/lib/error";
import { HOLIDAY_FIELDS } from "@/schemas/attendance/holiday.schema";
import useFetchBranches from "@/hooks/api/organization/organization-branches/useFetchBranches";
import useFetchDepartments from "@/hooks/api/organization/organization-departments/useFetchDepartments";

import useFetchHoliday, {
  type HolidayOrdering,
} from "@/hooks/api/holiday/useFetchHoliday";
import useCreateHoliday from "@/hooks/api/holiday/useCreateHoliday";
import useUpdateHoliday from "@/hooks/api/holiday/useUpdateHoliday";
import useDeleteHoliday from "@/hooks/api/holiday/useDeleteHolidy";

import { HolidayTable } from "@/components/crud/table/HolidayTable";
import { HolidayCalendarView } from "@/components/crud/table/HolidayCalendarView";
import { HolidayFormSheet } from "@/components/crud/forms/HolidayFormSheet";

import type { Holiday, HolidayPayload } from "@/types/holiday";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { CalendarDays, TreePalm } from "lucide-react";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

export const Route = createFileRoute("/admin/_layout/calendar")({
  validateSearch: z.object({
    tab: z.string().min(1).catch("general").default("general"),
  }),
  component: AdminCalendarPage,
});

function AdminCalendarPage() {
  const { tab } = Route.useSearch();
  const location = useLocation();
  const basePath = location.pathname;

  // "general" is the holiday month grid, "holiday" the filterable table.
  const isCalendarTab = tab !== "holiday";

  const sidebarNavItems: SidebarNavEntry[] = [
    {
      icon: <CalendarDays className="size-4" />,
      title: "General",
      href: `${basePath}?tab=general`,
      value: "general",
    },
    {
      icon: <TreePalm className="size-4" />,
      title: "Holiday",
      href: `${basePath}?tab=holiday`,
      value: "holiday",
    },
  ];

  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "attendance.holiday.manage");
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // FILTERS
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        date: SortDirection;
      },
      HolidayOrdering
    >({
      date: null,
    });

  const [statusFilter, setStatusFilter] = useState<
    "true" | "false" | undefined
  >();

  const [genderFilter, setGenderFilter] = useState<
    "Male" | "Female" | "Other" | undefined
  >();

  // The API filters take a single PK per dimension, so these stay single-select.
  const [branchFilter, setBranchFilter] = useState<string | undefined>();
  const [departmentFilter, setDepartmentFilter] = useState<
    string | undefined
  >();

  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [startDateFilter, setStartDateFilter] = useState<string | undefined>();
  const [endDateFilter, setEndDateFilter] = useState<string | undefined>();

  // Range of the visible calendar month (AD equivalents of BS-1 → BS-last in
  // Nepali mode), reported by the calendar view and used to scope its fetch.
  const [calendarRange, setCalendarRange] = useState<{
    start_date: string;
    end_date: string;
  } | null>(null);
  const handleCalendarMonthChange = useCallback(
    (start_date: string, end_date: string) => {
      setCalendarRange({ start_date, end_date });
    },
    [],
  );

  useEffect(() => {
    setStartDateFilter(
      dateRange?.from ? dayjs(dateRange.from).format("YYYY-MM-DD") : undefined
    );
    setEndDateFilter(
      dateRange?.to ? dayjs(dateRange.to).format("YYYY-MM-DD") : undefined
    );
  }, [dateRange]);

  //UI STATES
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [editingHolidayId, setEditingHolidayId] = useState<number | null>(null);
  const [calendarDefaultDate, setCalendarDefaultDate] = useState<
    string | undefined
  >();
  const [holidayToDelete, setHolidayToDelete] = useState<Holiday | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(globalFilter, 500);

  // QUERIES & MUTATIONS
  const holidaysQuery = useFetchHoliday({
    page,
    // Load all for calendar so every month is populated
    page_size: isCalendarTab ? 100 : pageSize,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    is_active:
      statusFilter === "true"
        ? true
        : statusFilter === "false"
          ? false
          : undefined,
    start_date: isCalendarTab ? calendarRange?.start_date : startDateFilter,
    end_date: isCalendarTab ? calendarRange?.end_date : endDateFilter,
    applicable_gender: genderFilter,
    applicable_branches: branchFilter ? Number(branchFilter) : undefined,
    applicable_departments: departmentFilter
      ? Number(departmentFilter)
      : undefined,
  });
  const holidays = holidaysQuery.data?.results ?? [];
  const count = holidaysQuery.data?.count ?? 0;

  // Fetched once here and handed down as lookup maps so the table and calendar
  // can name a holiday's scope without a request per row.
  const branchesQuery = useFetchBranches({ is_active: true, page_size: 200 });
  const departmentsQuery = useFetchDepartments({ page_size: 200 });
  const branches = useMemo(
    () => branchesQuery.data?.results ?? [],
    [branchesQuery.data],
  );
  const departments = useMemo(
    () => departmentsQuery.data?.results ?? [],
    [departmentsQuery.data],
  );
  const branchNames = useMemo(
    () => new Map(branches.map((b) => [b.id, b.name])),
    [branches],
  );
  const departmentNames = useMemo(
    () =>
      new Map(departments.map((d) => [d.id, `${d.name} (${d.branch_name})`])),
    [departments],
  );

  const createHoliday = useCreateHoliday();
  const updateHoliday = useUpdateHoliday();
  const deleteHoliday = useDeleteHoliday();

  const isMutating =
    createHoliday.isPending || updateHoliday.isPending || deleteHoliday.isPending;
  const isLoading = holidaysQuery.isPending || isMutating;
  const isFetching = holidaysQuery.isFetching || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    ordering,
    statusFilter,
    genderFilter,
    branchFilter,
    departmentFilter,
    startDateFilter,
    endDateFilter,
    isCalendarTab,
    setPage,
  ]);

  //RESET FILTERS
  const resetFilters = () => {
    setOrdering({ date: null });
    setStatusFilter(undefined);
    setGenderFilter(undefined);
    setBranchFilter(undefined);
    setDepartmentFilter(undefined);
    setDateRange(undefined);
    setStartDateFilter(undefined);
    setEndDateFilter(undefined);
    setGlobalFilter("");
    resetDebounce();
    setPage(1);
  };

  //HANDLERS
  const handleCreate = () => {
    setEditingHoliday(null);
    setEditingHolidayId(null);
    setCalendarDefaultDate(undefined);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleCreateFromCalendar = (date: Date) => {
    setEditingHoliday(null);
    setEditingHolidayId(null);
    setCalendarDefaultDate(dayjs(date).format("YYYY-MM-DD"));
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleEdit = (holiday: Holiday) => {
    setEditingHolidayId(holiday.id);
    setEditingHoliday({ ...holiday });
    setIsViewOnly(false); // Reset to false
    setIsSheetOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!holidayToDelete) return;
    try {
      await deleteHoliday.mutateAsync({ id: holidayToDelete.id });
      toast.success("Holiday deleted successfully!");
      setIsDeleteDialogOpen(false);
      setHolidayToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleFormSubmit = async (
    data: HolidayPayload,
    setError: UseFormSetError<HolidayPayload>
  ) => {
    try {
      if (editingHolidayId) {
        await updateHoliday.mutateAsync({ id: editingHolidayId, payload: data });
        toast.success("Holiday updated successfully!");
      } else {
        await createHoliday.mutateAsync(data);
        toast.success("Holiday created successfully!");
      }
      setIsSheetOpen(false);
    } catch (err) {
      const { handled, rest } = applyServerErrors(err, setError, HOLIDAY_FIELDS);
      if (!handled || rest.length) {
        toast.error(rest.join(" | ") || formatError(err));
      }
    }
  };

  // PAGINATION

  const totalPages = useMemo(
    () => (count ? Math.ceil(count / pageSize) : 1),
    [count, pageSize]
  );

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const [showFilters, setShowFilters] = useState(false);

  const [isViewOnly, setIsViewOnly] = useState(false);

  const handleView = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setIsViewOnly(true); // Turn on view mode
    setIsSheetOpen(true);
  };

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Calendar"
          description="View the holiday calendar and manage employee holidays"
          onToggleFilter={
            isCalendarTab ? undefined : () => setShowFilters((prev) => !prev)
          }
          onAdd={canManage ? handleCreate : undefined}
          addLabel="Create Holiday"
          isLoading={isFetching}
        />

        <div className="flex flex-col space-y-8 xl:flex-row xl:space-x-12 xl:space-y-0">
          <aside className="xl:w-42 mb-2 xl:mr-5">
            <SidebarNav
              items={sidebarNavItems}
              activeValue={isCalendarTab ? "general" : "holiday"}
              widthClassName="xl:w-44"
              breakpoint="xl"
            />
          </aside>
          <div className="flex-1 min-w-0 space-y-4">
            {!isCalendarTab && (
              <FiltersPanel
                show={showFilters}
                onClear={resetFilters}
                isLoading={isFetching}
              >
                <SearchFilter
                  placeholder="Search Holidays..."
                  value={globalFilter}
                  onChange={setGlobalFilter}
                />
                <SelectFilter
                  placeholder="Status"
                  options={[
                    { label: "Active", value: "true" },
                    { label: "Not Active", value: "false" },
                  ]}
                  value={statusFilter}
                  onChange={setStatusFilter}
                />
                <SelectFilter
                  placeholder="Gender"
                  options={[
                    { label: "Male", value: "Male" },
                    { label: "Female", value: "Female" },
                    { label: "Other", value: "Other" },
                  ]}
                  value={genderFilter}
                  onChange={setGenderFilter}
                />
                <SelectFilter
                  placeholder="Branch"
                  options={branches.map((b) => ({
                    label: b.name,
                    value: String(b.id),
                  }))}
                  value={branchFilter}
                  onChange={setBranchFilter}
                />
                <SelectFilter
                  placeholder="Department"
                  options={departments.map((d) => ({
                    label: `${d.name} (${d.branch_name})`,
                    value: String(d.id),
                  }))}
                  value={departmentFilter}
                  onChange={setDepartmentFilter}
                />

                <DateSelector
                  value={dateRange}
                  onApply={(range) => setDateRange(range)}
                  onClear={() => setDateRange(undefined)}
                />
              </FiltersPanel>
            )}

            {isCalendarTab ? (
              <>
                <style>{`
                  .holiday-calendar-wrap { height: calc(100vh - 220px); min-height: 600px; }
                  .holiday-calendar-wrap .rbc-calendar { height: 100% !important; }
                `}</style>
                <div className="holiday-calendar-wrap">
                  <HolidayCalendarView
                    holidays={holidays}
                    loading={isLoading}
                    onEventClick={handleView}
                    onAdd={canManage ? handleCreateFromCalendar : undefined}
                    onEdit={canManage ? handleEdit : undefined}
                    onMonthChange={handleCalendarMonthChange}
                  />
                </div>
              </>
            ) : (
              <>
                <HolidayTable
                  holidays={holidays}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDelete={(h) => {
                    setHolidayToDelete(h);
                    setIsDeleteDialogOpen(true);
                  }}
                  isLoading={isLoading}
                  ordering={ordering}
                  onOrderingChange={toggleOrdering}
                  branchNames={branchNames}
                  departmentNames={departmentNames}
                  canManage={canManage}
                />
                <PaginationComponent
                  count={count}
                  page={page}
                  onPageChange={handlePageChange}
                  isLoading={isFetching}
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                />
              </>
            )}
          </div>
        </div>

        <HolidayFormSheet
          isOpen={isSheetOpen}
          onClose={() => {
            setIsSheetOpen(false);
            setCalendarDefaultDate(undefined);
          }}
          onSubmit={handleFormSubmit}
          editingHoliday={editingHoliday}
          isViewOnly={isViewOnly}
          isLoading={isMutating}
          defaultDate={calendarDefaultDate}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Holiday"
          description={`Are you sure you want to delete "${holidayToDelete?.name}"?`}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
